import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, StatCard } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { Table, Td, Th } from "@/components/ui/Table";
import { useToast } from "@/hooks/useToast";
import { admin as adminApi } from "@/lib/api";
import { formatBytes, formatDateTime, formatRelative } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { SessionRecord, SystemHealth } from "@/lib/types";

/** The Administrator's operations page (docs/08 §8.12, A-06). */
export function SystemHealthView() {
  const { notify } = useToast();
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);

  const load = useCallback(async () => {
    try {
      const [healthData, sessionData] = await Promise.all([
        adminApi.health(),
        adminApi.sessions(true),
      ]);
      setHealth(healthData);
      setSessions(sessionData);
    } catch {
      notify(strings.errors.forbidden, "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
    // A-06: the numbers on this page go stale quickly.
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const runBackup = async () => {
    setBackingUp(true);
    try {
      const result = await adminApi.backup();
      notify(result.message, "success");
    } catch {
      notify("Could not start the backup. Check BACKUP_SCRIPT on the server.", "error");
    } finally {
      setBackingUp(false);
    }
  };

  const revoke = async (session: SessionRecord) => {
    await adminApi.revokeSession(session.id);
    notify("Session revoked. It stops working on the next request.", "success");
    await load();
  };

  if (loading) return <Spinner label="Loading system health" />;
  if (!health) return null;

  const backupIsStale =
    !health.last_backup_at ||
    Date.now() - new Date(health.last_backup_at).getTime() > 26 * 3600 * 1000;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">
          {strings.admin.systemHealth}
        </h1>
        <div className="flex items-center gap-2">
          <Badge tone={health.environment === "production" ? "brand" : "warning"}>
            {health.environment}
          </Badge>
          <Badge>v{health.version}</Badge>
          <Button size="sm" loading={backingUp} onClick={() => void runBackup()}>
            Run a backup now
          </Button>
        </div>
      </div>

      {backupIsStale && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-900">
          No successful backup in the last 26 hours. Check the cron container
          before anything else.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Isolates" value={health.isolate_count.toLocaleString()} />
        <StatCard label="Database size" value={formatBytes(health.database_size_bytes)} />
        <StatCard
          label="Database connections"
          value={health.connection_count ?? "—"}
        />
        <StatCard
          label="Error rate (24h)"
          value={`${(health.error_rate_24h * 100).toFixed(2)}%`}
          tone={health.error_rate_24h > 0.01 ? "blocking" : "ok"}
          hint="Target is under 1%"
        />
        <StatCard label="Active sessions" value={health.active_sessions} />
        <StatCard label="Live update streams" value={health.sse_subscribers} />
        <StatCard
          label="Last backup"
          value={health.last_backup_at ? formatRelative(health.last_backup_at) : "Never"}
          tone={backupIsStale ? "blocking" : "ok"}
        />
      </div>

      <Card>
        <CardHeader
          title={strings.admin.sessions}
          description="Revoking a session signs that device out on its very next request."
        />
        {sessions.length === 0 ? (
          <CardBody>
            <p className="text-sm text-slate-500">Nobody is signed in.</p>
          </CardBody>
        ) : (
          <Table caption="Active sessions">
            <thead>
              <tr>
                <Th>Signed in</Th>
                <Th>Last seen</Th>
                <Th>Expires</Th>
                <Th>From</Th>
                <Th>Device</Th>
                <Th><span className="sr-only">Actions</span></Th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id}>
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(session.issued_at)}
                  </Td>
                  <Td className="whitespace-nowrap text-xs">
                    {session.last_seen_at ? formatRelative(session.last_seen_at) : "—"}
                  </Td>
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(session.expires_at)}
                  </Td>
                  <Td className="font-mono text-xs">{session.ip_address ?? "—"}</Td>
                  <Td className="max-w-xs truncate text-xs text-slate-500">
                    {session.user_agent ?? "—"}
                  </Td>
                  <Td>
                    <Button size="sm" variant="ghost" onClick={() => void revoke(session)}>
                      {strings.admin.forceLogout}
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
